import constants from '../const.js';

const VIEWS_URL = `${constants.USER_API.USER_API_URL}/users/history/views`;
const FLUSH_INTERVAL_MS = 5000;
const FLUSH_SIZE = 20;
const MAX_QUEUE = 200;
// Must stay in sync with routes in components/App.js and SCREENS in userAPI routes/history.js
const DETAIL_SCREENS = new Set(['scene', 'movie', 'actor', 'gallery', 'playlist', 'profile']);
const SCREEN_PATTERN = /^\/([a-z-]*)(?:\/([^/]+))?\/?$/;
// Gallery IDs are folder names, and the open album is in ?album=
const FILTER_SCREENS = new Set(['scenes', 'actors', 'scene-movies', 'gallery']);
// Pagination is not a filter; -1/empty are the "no filter" defaults used in scenes.js
const IGNORED_PARAMS = new Set(['page', 'size', 'search']);
const EMPTY_VALUES = new Set(['', '-1', 'null', 'undefined']);

function readFilters(params) {
  const filters = {};
  for (const rawKey of new Set(params.keys())) {
    const key = rawKey.replace(/\[\]$/, '');
    if (IGNORED_PARAMS.has(key)) continue;
    const values = params.getAll(rawKey).map((v) => v.trim().slice(0, 200)).filter((v) => !EMPTY_VALUES.has(v));
    if (values.length === 0) continue;
    filters[key] = values.length === 1 && !rawKey.endsWith('[]') && key !== 'tag' && key !== 'model'
      ? values[0]
      : values.slice(0, 50);
  }
  return Object.keys(filters).length ? filters : null;
}

let queue = [];
let started = false;
let lastKey = null;

function enqueue(event) {
  queue.push({ ts: Date.now(), ...event });
  if (queue.length > MAX_QUEUE) queue = queue.slice(-MAX_QUEUE);
  if (queue.length >= FLUSH_SIZE) flush();
}

function currentScreen() {
  const [route, query = ''] = window.location.hash.replace(/^#/, '').split('?');
  const m = (route || '/').match(SCREEN_PATTERN);
  if (!m) return null;
  const screen = m[1] || 'home';
  let id = DETAIL_SCREENS.has(screen) && m[2] ? m[2] : null;
  if (id) {
    try { id = decodeURIComponent(id); } catch { /* keep raw */ }
    id = id.slice(0, 200);
  }
  const params = new URLSearchParams(query);
  const search = (params.get('search') || '').trim();
  const filters = FILTER_SCREENS.has(screen) ? readFilters(params) : null;
  return { screen, id, search: search ? search.slice(0, 200) : null, filters };
}

function flush() {
  if (queue.length === 0) return;
  const token = localStorage.getItem('token');
  if (!token) {
    queue = [];
    return;
  }
  const views = queue.splice(0, 100);
  fetch(VIEWS_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: token },
    body: JSON.stringify({ views }),
    keepalive: true,
  }).catch(() => {});
}

function onRouteChange() {
  const view = currentScreen();
  // Paging changes keep the same key so they are not logged again
  const key = view ? JSON.stringify(view) : null;
  if (key === lastKey) return;
  lastKey = key;
  if (view) enqueue(view);
}

// For searches that don't change the URL (e.g. in-gallery image search)
export function trackSearch(search) {
  const view = currentScreen();
  const term = typeof search === 'string' ? search.trim() : '';
  if (!view || !term) return;
  enqueue({ ...view, search: term.slice(0, 200) });
}

export function startViewTracking() {
  if (started) return;
  started = true;
  onRouteChange();
  window.addEventListener('hashchange', onRouteChange);
  setInterval(flush, FLUSH_INTERVAL_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
  window.addEventListener('pagehide', flush);
}
